import { ColDef } from "@ag-grid-community/core";
import { get, isFunction, isNil } from "lodash-es";
import { MessageDescriptor } from "react-intl";
import invariant from "tiny-invariant";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import SchemaFieldCategory from "@mapmycustomers/shared/enum/SchemaFieldCategory";
import SchemaFieldType from "@mapmycustomers/shared/enum/SchemaFieldType";
import FormProperties from "@mapmycustomers/shared/types/fieldModel/FormProperties";
import IField, { CsvExportProperties } from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import IFiltersConfig, {
  FilterInstanceType,
  FilterOptionValue,
  IFilterInstanceConfig,
  IHumanReadableFilterConfig,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import ImportProperties from "@mapmycustomers/shared/types/fieldModel/ImportProperties";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import {
  CombinedCondition,
  FilterModel,
  LegacyFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import PlatformFilterModel, {
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import i18nService from "@app/config/I18nService";
import defaultFilters from "@app/util/filters/defaultFilters";
import loggingService from "@app/util/logging";
import { getFilterOperatorDisplayName } from "@app/util/ui";
import {
  isCombinedPlatformCondition,
  isLegacyCombinedCondition,
  isLegacySimpleCondition,
  isSimplePlatformCondition,
} from "@app/util/viewModel/assert";
import {
  getRegularFieldConditionValue,
  platformCombineOperatorToFilterCombineOperator,
} from "@app/util/viewModel/convertFromPlatformFilterModel";
import {
  filterCombineOperatorToPlatformCombineOperator,
  getRegularFieldPlatformConditionValue,
} from "@app/util/viewModel/convertToPlatformFilterModel";

import { isFilterInstanceConfig, isFilterOperatorConfig } from "./assert";
import csvFormatterByType from "./csvFormatterByType";
import {
  defaultTooltipValueGetter,
  valueFormatterUsingFunction,
  valueGetterUsingFunction,
} from "./fieldUtil";

export type FieldValueGetter = ((entity: unknown, field: Field) => any) | string | string[];
export type FieldValueFormatter = (entity: unknown, value: unknown) => string;

export interface FieldProperties {
  csvExportProperties?: CsvExportProperties;
  customFilterConfig?: Partial<IHumanReadableFilterConfig>;
  customGridProperties?: ColDef;
  displayName: MessageDescriptor | string;
  displayOrder?: number;
  exportColumnName?: string;
  extraPlatformFiltersGetter?: () => PlatformFilterModel;
  features?: FieldFeature[];
  fieldManagementDisplayName?: MessageDescriptor;
  filterName?: string;
  filterType?: FieldType;
  formProperties?: FormProperties;
  importProperties?: ImportProperties;
  name: string;
  overridePhiSupport?: boolean;
  platformAreaFieldName?: string;
  platformFilterNames?: string[];
  platformName?: string;
  sortName?: string;
  type: FieldType;
  valueFormatter?: FieldValueFormatter;
  valueGetter?: FieldValueGetter;
}

class Field implements IField {
  protected readonly _name: string;
  protected _filterName: string;
  protected _platformFilterNames: string[];
  protected _platformAreaFieldName: string | undefined;
  protected _platformName: string;
  protected _exportColumnName: string;
  protected readonly _displayName: MessageDescriptor | string;
  protected readonly _sortName: string | undefined;
  protected readonly _fieldManagementDisplayName?: MessageDescriptor;
  private readonly _displayOrder: number | undefined;
  private readonly _extraPlatformFiltersGetter: (() => PlatformFilterModel) | undefined;
  protected readonly _type: FieldType;
  protected readonly _filterType: FieldType;
  protected readonly _valueFormatter: FieldValueFormatter | undefined;
  protected readonly _valueGetter: FieldValueGetter | undefined;
  protected readonly _importProperties?: ImportProperties;
  protected readonly _formProperties?: FormProperties;
  protected readonly _csvExportProperties?: CsvExportProperties;
  protected _features!: Set<FieldFeature>;
  protected _customGridProperties?: ColDef;
  protected _schemaDetails?: SchemaField;
  protected _filter!: false | IFiltersConfig;
  protected readonly _overridePhiSupport?: boolean;

  constructor(props: FieldProperties) {
    const {
      csvExportProperties,
      displayName,
      displayOrder,
      exportColumnName,
      extraPlatformFiltersGetter,
      fieldManagementDisplayName,
      filterName,
      filterType,
      formProperties,
      importProperties,
      name,
      overridePhiSupport,
      platformAreaFieldName,
      platformFilterNames,
      platformName,
      sortName,
      type,
      valueFormatter,
      valueGetter,
      ...restProps
    } = props;
    this._name = name;
    this._filterName = filterName ?? name;
    this._sortName = sortName;
    this._platformFilterNames = platformFilterNames ?? (filterName ? [filterName] : [name]);
    this._platformAreaFieldName = platformAreaFieldName;
    this._platformName = platformName ?? name;
    this._exportColumnName = exportColumnName ?? name;
    this._displayName = displayName;
    this._fieldManagementDisplayName = fieldManagementDisplayName;
    this._displayOrder = displayOrder;
    this._extraPlatformFiltersGetter = extraPlatformFiltersGetter;
    this._type = type;
    this._filterType = filterType ?? type;
    this._valueFormatter = valueFormatter;
    this._valueGetter = valueGetter;
    this._importProperties = importProperties;
    this._formProperties = formProperties;
    this._csvExportProperties = csvExportProperties;
    this._overridePhiSupport = overridePhiSupport;
    this.update(restProps);
  }

  update(props: Omit<FieldProperties, "displayName" | "name" | "type">) {
    const { customFilterConfig, customGridProperties, features } = props;
    this._features = new Set(features);
    this._customGridProperties = customGridProperties;
    this._filter = this.processInitialFilterConfig(customFilterConfig);
  }

  setSchema(schemaDetails: SchemaField) {
    this._schemaDetails = schemaDetails;
  }

  get name(): string {
    return this._name;
  }

  get filterName(): string {
    return this._filterName;
  }

  get platformFilterNames(): string[] {
    return this._platformFilterNames;
  }

  get platformAreaFieldName(): string | undefined {
    return this._platformAreaFieldName;
  }

  get platformName(): string {
    return this._platformName;
  }

  get exportColumnName(): string {
    return this._exportColumnName;
  }

  get sortName(): string {
    return this._sortName ?? this.name;
  }

  get schemaDetails(): SchemaField | undefined {
    return this._schemaDetails;
  }

  get displayName(): string {
    return typeof this._displayName === "string"
      ? this._displayName // designed for using with Custom Fields
      : (i18nService.getIntl()?.formatMessage(this._displayName) ?? "");
  }

  get exportDisplayName(): string {
    return this._csvExportProperties?.displayName
      ? (i18nService.getIntl()?.formatMessage(this._csvExportProperties.displayName) ??
          this.displayName)
      : this.displayName;
  }

  get displayOrder(): number | undefined {
    return this._displayOrder;
  }

  get filter(): false | IFiltersConfig {
    return this._filter;
  }

  get isGroupField(): boolean {
    return false;
  }

  get type(): FieldType {
    return this._type;
  }

  get filterType(): FieldType {
    return this._filterType;
  }

  get extraPlatformFilters(): PlatformFilterModel {
    return this._extraPlatformFiltersGetter ? this._extraPlatformFiltersGetter() : {};
  }

  get integrationName(): string {
    return this._name;
  }

  get emailDynamicVariableName(): string {
    return this._name;
  }

  get formProperties(): FormProperties | undefined {
    return this._formProperties;
  }

  get importProperties(): ImportProperties | undefined {
    return this._importProperties;
  }

  get importName(): string {
    return this.importProperties?.name || this.name;
  }

  get fieldManagementDisplayName(): string {
    if (this._fieldManagementDisplayName) {
      return i18nService.getIntl()?.formatMessage(this._fieldManagementDisplayName) ?? "";
    }

    return this.displayName;
  }

  get importDisplayName(): string {
    if (this.importProperties?.displayName) {
      return i18nService.getIntl()?.formatMessage(this.importProperties.displayName) ?? "";
    }

    return this.displayName;
  }

  gridProperties(forClientSideGrid?: boolean): ColDef {
    return this.buildGridProperties(forClientSideGrid, this._customGridProperties);
  }

  hasFeature = (feature: FieldFeature): boolean => this._features.has(feature);
  hasAllFeatures = (...features: FieldFeature[]): boolean =>
    features.every((feature) => this._features.has(feature));
  hasAnyFeature = (...features: FieldFeature[]): boolean =>
    features.some((feature) => this._features.has(feature));

  toggleFeature = (feature: FieldFeature, present: boolean) => {
    if (present) {
      this._features.add(feature);
    } else {
      this._features.delete(feature);
    }
  };

  /**
   * Returns column's value from the given entity object
   * @param {unknown} entity
   * @returns {*}
   */
  getValueFor(entity: unknown): unknown {
    return isFunction(this._valueGetter)
      ? this._valueGetter(entity, this)
      : get(entity, this._valueGetter || this.name);
  }

  getFormattedValueFor(entity: unknown): string {
    const value = this.getValueFor(entity);
    return this.formatValue(entity, value);
  }

  hasNonEmptyValueFor(entity: unknown): boolean {
    const value = this.getValueFor(entity);
    return !(
      isNil(value) ||
      (Array.isArray(value) && !value.length) ||
      (typeof value === "string" && value.trim() === "")
    );
  }

  getHumanReadableDescription(
    value: LegacyFilterCondition | undefined,
    filterOptions?: Partial<Record<FilterOption, FilterOptionValue>>
  ): string | undefined {
    if (!value || !this._filter) {
      return undefined;
    }

    if (isLegacyCombinedCondition(value)) {
      const nestedConditionsDescriptions = value.conditions.map((condition) =>
        this.getHumanReadableDescription(condition, filterOptions)
      );
      // TODO: translate AND/OR operator
      return `(${nestedConditionsDescriptions.join(` ${value.operator} `)})`;
    }

    const operator = this._filter.operators.find((config) => config.operator === value.operator);
    if (!operator) {
      loggingService.error(`Unknown operator applied to field ${this.name}: "${value.operator}"`);
      return undefined;
    }

    const instance = this._filter.filterInstances[operator.instance];
    if (!instance) {
      loggingService.error(
        `Unknown instance referred in operator in field ${this.name}: "${operator.instance}"`
      );
      return undefined;
    }

    if (instance.instance.getHumanReadableDescription) {
      return instance.instance.getHumanReadableDescription(value, this, {
        ...filterOptions,
        ...instance.options,
      });
    }

    // use default filter formatting which is fieldName-operator-value
    const intl = i18nService.getIntl();
    const formattedOperator = intl
      ? getFilterOperatorDisplayName(intl, value.operator, this)
      : value.operator;
    const formattedValue = value.value ? ` ${String(value.value)}` : "";
    return `${this.displayName} ${formattedOperator}${formattedValue}`;
  }

  setFilterOption(option: FilterOption, value: FilterOptionValue) {
    if (!this._filter) {
      return;
    }

    Object.keys(this._filter.filterInstances).forEach((instanceName) => {
      const instance = (this._filter as IFiltersConfig).filterInstances[instanceName];
      if (instance.instance.doesSupportOption?.(option)) {
        instance.options = { ...(instance.options ?? {}), [option]: value };
      }
    });
  }

  isFilterEnabled(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    currentFilters: FilterModel,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    currentFilterGroup: CombinedCondition,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fieldModel: IFieldModel
  ): string | true {
    return true; // feel free to override in successors
  }

  convertToPlatformCondition(filterCondition: LegacyFilterCondition): PlatformFilterCondition {
    if (isLegacyCombinedCondition(filterCondition)) {
      const operator = filterCombineOperatorToPlatformCombineOperator[filterCondition.operator];
      return {
        [operator]: filterCondition.conditions.map((simpleCondition) => ({
          [this._platformFilterNames[0]]: getRegularFieldPlatformConditionValue(
            this,
            simpleCondition
          ),
        })),
      };
    } else {
      return {
        [this._platformFilterNames[0]]: getRegularFieldPlatformConditionValue(
          this,
          filterCondition
        ),
      };
    }
  }

  convertFromPlatformCondition(
    filterCondition: PlatformFilterCondition
  ): LegacyFilterCondition | undefined {
    if (isCombinedPlatformCondition(filterCondition)) {
      const platformOperator = "$and" in filterCondition ? "$and" : "$or";
      const operator = platformCombineOperatorToFilterCombineOperator[platformOperator];

      let conditions = filterCondition[platformOperator]!.filter(isSimplePlatformCondition) // we do not support nested combined conditions
        .map((simpleCondition) => getRegularFieldConditionValue(this, simpleCondition))
        .filter(isDefined);

      // only leave such combined conditions which have matching operator
      const combinedConditions = conditions
        .filter(isLegacyCombinedCondition)
        .filter((condition) => condition.operator === operator);
      combinedConditions.forEach((condition) => {
        conditions = conditions.concat(condition.conditions);
      });

      return { conditions: conditions.filter(isLegacySimpleCondition), operator };
    } else {
      return getRegularFieldConditionValue(this, filterCondition);
    }
  }

  isPresentInPlatformCondition(condition: Record<string, unknown>): boolean {
    return this.platformFilterNames.some((name) => name in condition);
  }

  getFilterConditionForMetaField(
    platformFilterModel: PlatformFilterModel
  ): PlatformFilterCondition {
    // using ! here because we assume isPresentInPlatformCondition method is called before getFilterConditionForMetaField
    const name = this.platformFilterNames.find((name) => name in platformFilterModel)!;
    return {
      [name]: platformFilterModel[name as keyof PlatformFilterModel],
    };
  }

  protected buildGridProperties(forClientSideGrid?: boolean, customGridProperties?: ColDef) {
    const gridProperties: ColDef = {
      colId: this.name,
      editable: false,
      filter: false, // we do not filter in grid anymore
      headerName: this.displayName,
      headerTooltip: this.displayName,
      menuTabs: ["generalMenuTab"],
      resizable: true,
      suppressAutoSize: false,
      suppressMenu: false,
      suppressSizeToFit: false,
      type: [String(this._type), ...Array.from(this._features)], // some column properties are set according to types, see Grid.tsx
      unSortIcon: false,
    };

    if (this._type === FieldType.NUMBER || this.hasFeature(FieldFeature.NUMERIC)) {
      gridProperties.cellClass = "mmc-text-align-right";
    }

    gridProperties.tooltipValueGetter = defaultTooltipValueGetter(this.name);
    gridProperties.valueGetter = valueGetterUsingFunction(this.getValueFor.bind(this));
    gridProperties.valueFormatter = valueFormatterUsingFunction(this.formatValue.bind(this));

    return Object.assign(gridProperties, customGridProperties);
  }

  /**
   * Returns formatted value from the given entity object and value.
   * @param {Object} entity
   * @param {*} value parsed value
   * @returns {string}
   */
  protected formatValue(entity: unknown, value: unknown): string {
    return this._valueFormatter ? this._valueFormatter(entity, value) : String(value ?? "");
  }

  // Convert given config filter into IFiltersConfig or false if filter is not given. Also, validate config.
  protected processInitialFilterConfig(
    customFilter?: Partial<IHumanReadableFilterConfig>
  ): false | IFiltersConfig {
    if (!this.hasFeature(FieldFeature.FILTERABLE) && !customFilter) {
      return false;
    }

    // not using deepmerge here to avoid merging unwanted props
    const filter: Partial<IHumanReadableFilterConfig> = {
      ...(defaultFilters[this._type] || {}),
      ...(customFilter || {}),
    };

    // no config after all, means no filter
    // TODO: consider converting this into invariant after all new filters are done
    // because basically, it should _either_ have default or custom filter or NOT have FILTERABLE feature
    if (!Object.keys(filter).length) {
      return false;
    }

    invariant(
      filter.filterInstances && Object.keys(filter.filterInstances).length > 0,
      `Filter instances are not defined for field "${this.name}"`
    );
    invariant(
      filter.operators && filter.operators.length > 0,
      `Filter operators are not defined for field "${this.name}"`
    );

    const instanceNames = Object.keys(filter.filterInstances);
    const defaultInstance = filter.defaultInstance ?? instanceNames[0]; // take first instance if default instance is not specified
    invariant(
      instanceNames.includes(defaultInstance),
      `Invalid default instance: "${defaultInstance}". It is not present in instances for field "${this.name}".`
    );

    // iterate over all instances to guarantee each item is of IFilterInstanceConfig type
    const instances: Record<FilterInstanceType, IFilterInstanceConfig> = instanceNames.reduce(
      (result, instanceName) => {
        const item = filter.filterInstances![instanceName];
        return {
          ...result,
          [instanceName]: isFilterInstanceConfig(item) ? item : { instance: item },
        };
      },
      {}
    );

    // iterate over all operators to guarantee
    const operators = filter.operators.map((operator) =>
      isFilterOperatorConfig(operator) ? operator : { instance: defaultInstance, operator }
    );

    let defaultOperator = operators[0].operator;
    if (filter.defaultOperator) {
      invariant(
        operators.some(({ operator }) => operator === filter.defaultOperator),
        `Invalid default operator: "${filter.defaultOperator}". Not present in operators list for field "${this.name}"`
      );
      defaultOperator = filter.defaultOperator;
    }

    return {
      defaultOperator,
      disallowedConjunctions: customFilter?.disallowedConjunctions,
      filterInstances: instances,
      oneOccurrenceOnly: customFilter?.oneOccurrenceOnly,
      operators,
      topLevelOnly: customFilter?.topLevelOnly,
    };
  }

  getFormattedCsvValueFor(entity: unknown) {
    const value = this.getValueFor(entity);

    const formatter = this._csvExportProperties?.valueFormatter ?? csvFormatterByType[this.type];
    if (formatter) {
      return formatter(entity, value);
    }
    return this.formatValue(entity, value);
  }

  get isCustomizableField() {
    return !this.hasAnyFeature(
      FieldFeature.NON_LIST_VIEW,
      FieldFeature.NON_MAP_VIEW,
      FieldFeature.MAP_PINNED_FIELD
    );
  }

  // schema related getters:

  get isArchived() {
    return this._schemaDetails?.archived === true;
  }

  get doesSupportPhi() {
    if (this._overridePhiSupport !== undefined) {
      return this._overridePhiSupport;
    }
    if (
      this.hasAnyFeature(
        FieldFeature.ASSOCIATED_ENTITY,
        FieldFeature.GROUP_FIELD,
        FieldFeature.ACTIVITY_TYPE_FIELD,
        FieldFeature.FUNNEL_FIELD,
        FieldFeature.STAGE_FIELD
      ) ||
      [
        SchemaFieldCategory.GROUP,
        SchemaFieldCategory.RELATIONSHIP,
        SchemaFieldCategory.SYSTEM_DEPENDENT,
        SchemaFieldCategory.SYSTEM_REQUIRED,
        SchemaFieldCategory.VARIANT,
      ].includes(this.platformFieldCategory)
    ) {
      return false;
    }
    return true;
  }

  get isArchivable() {
    return (
      this._schemaDetails?.canArchive === true &&
      // platform currently returns canArchive=true for system-dependent fields, so we need this condition
      this.platformFieldCategory !== SchemaFieldCategory.SYSTEM_DEPENDENT
    );
  }

  get isCompound() {
    return this._schemaDetails?.compound === true;
  }

  get isDefaultValueAllowed() {
    return this._schemaDetails?.isDefaultValueAllowed === true;
  }

  get isEditable() {
    return this._schemaDetails?.accessStatus.update !== false;
  }

  get isEssential() {
    return [
      SchemaFieldCategory.SYSTEM_DEPENDENT,
      SchemaFieldCategory.SYSTEM_REQUIRED,
      SchemaFieldCategory.VARIANT,
    ].includes(this.platformFieldCategory);
  }

  get isNullable() {
    return (
      ![SchemaFieldCategory.SYSTEM_REQUIRED, SchemaFieldCategory.VARIANT].includes(
        this.platformFieldCategory
      ) && !this.hasFeature(FieldFeature.BULK_ADDRESS)
    );
  }

  get isMovableInLayout() {
    return !this.isEssential;
  }

  get isDeletableInLayout() {
    return !this.isEssential;
  }

  get isPhiEnabled() {
    return this._schemaDetails?.phiEnabled === true;
  }

  get isReadable() {
    // system-required fields are always readable
    if (this.isSystemRequired) {
      return true;
    }
    return this._schemaDetails?.accessStatus.read !== false;
  }

  get isSystemRequired() {
    return (
      this.platformFieldCategory === SchemaFieldCategory.SYSTEM_REQUIRED ||
      this.platformFieldCategory === SchemaFieldCategory.VARIANT
    );
  }

  get isUsedInCalculatedFields() {
    return this._schemaDetails?.usedInCalculatedFields === true;
  }

  get isUsedInWorkflow() {
    return this._schemaDetails?.usedInWorkflow === true;
  }

  get platformFieldType() {
    if (this._schemaDetails) {
      return this._schemaDetails.fieldType;
    }

    if (this.hasFeature(FieldFeature.CUSTOM_FIELD)) {
      return SchemaFieldType.CUSTOM;
    }

    if (this.hasFeature(FieldFeature.GROUP_FIELD)) {
      return SchemaFieldType.GROUP;
    }

    if (this.hasFeature(FieldFeature.FILE_FIELD)) {
      return SchemaFieldType.FILE;
    }

    return SchemaFieldType.STANDARD;
  }

  get platformFieldCategory() {
    if (this._schemaDetails?.category) {
      return this._schemaDetails.category;
    }

    if (this.hasFeature(FieldFeature.CUSTOM_FIELD)) {
      return SchemaFieldCategory.CUSTOM;
    }

    if (this.hasFeature(FieldFeature.GROUP_FIELD)) {
      return SchemaFieldCategory.GROUP;
    }

    if (this.hasFeature(FieldFeature.FILE_FIELD)) {
      return SchemaFieldCategory.FILE;
    }

    if (this.hasFeature(FieldFeature.ADDRESS)) {
      return SchemaFieldCategory.ADDRESS;
    }

    if (this.hasFeature(FieldFeature.ASSOCIATED_ENTITY)) {
      return SchemaFieldCategory.RELATIONSHIP;
    }

    if (
      this.hasAnyFeature(
        FieldFeature.FUNNEL_FIELD,
        FieldFeature.STAGE_FIELD,
        FieldFeature.ACTIVITY_TYPE_FIELD
      )
    ) {
      return SchemaFieldCategory.VARIANT;
    }

    return SchemaFieldCategory.STANDARD;
  }

  get isUnique() {
    return this.hasFeature(FieldFeature.UNIQUE);
  }
}

export default Field;
